import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { ChevronDown, ChevronLeft, Layers3, X } from "lucide-react";

import { StatusPill } from "@/components/common/status-pill";
import { GeneratePlannedPostSheet } from "@/components/campaign-review/generate-planned-post-sheet";
import {
  CurrentAttemptSummary,
  DetailBlock,
  EmptyReviewState,
  ReviewStatusNotice,
} from "@/components/campaign-review/planned-post-workspace-components";
import { useAdminCampaignReviewData } from "@/components/campaign-review/use-admin-campaign-review-data";
import {
  buildCampaignDetailPath,
  canRetryFailedGeneration,
  getPlannedPostPrimaryActionState,
  getPostTypeLabel,
  PLANNED_POST_STATUS_CONFIG,
} from "@/components/campaign-review/utils";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@runcheetah/ui";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { EMPTY_BRANDKIT } from "@/pages/social-post-planner/constants";
import { BrandkitPanel } from "@/pages/social-post-planner/components/brandkit-panel";
import {
  CarouselCopyPanel,
  CarouselSlideRail,
  CarouselSlideSummary,
  getCarouselReviewState,
} from "@/pages/social-post-planner/components/carousel-slide-review";
import { CopyTracePanel } from "@/pages/social-post-planner/components/copy-trace-panel";
import {
  PlannerOutput,
  type PlannerView,
} from "@/pages/social-post-planner/components/planner-output";
import { PlannerTaskList } from "@/pages/social-post-planner/components/planner-task-list";
import { SceneTemplatePreview } from "@/pages/social-post-planner/components/scene-template-preview";
import { TaskStatusBadge } from "@/pages/social-post-planner/components/task-status-badge";
import type { SceneBlock } from "@/pages/social-post-planner/types";
import { formatDate, getSceneBlocksFlat } from "@/pages/social-post-planner/utils";

export function CampaignPlannedPostDetailPage() {
  const {
    id: brandId,
    campaignId,
    postId,
  } = useParams<{
    id: string;
    campaignId: string;
    postId: string;
  }>();
  const [searchParams] = useSearchParams();
  const utils = trpc.useUtils();

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(searchParams.get("taskId"));
  const [selectedTaskCarouselSlideId, setSelectedTaskCarouselSlideId] = useState<string | null>(
    null,
  );
  const [reviewCarouselSlideId, setReviewCarouselSlideId] = useState<string | null>(null);
  const [selectedSceneBlockId, setSelectedSceneBlockId] = useState<string | null>(null);
  const [view, setView] = useState<PlannerView>(
    searchParams.get("view") === "technical" ? "technical" : "creative",
  );
  const [additionalDirection, setAdditionalDirection] = useState("");
  const [newAttemptSheetOpen, setNewAttemptSheetOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [reviewNotesOpen, setReviewNotesOpen] = useState(false);
  const [brandkitOpen, setBrandkitOpen] = useState(false);

  const {
    brandQuery,
    campaignQuery,
    brand,
    campaign,
    tasks,
    tasksQuery,
    campaignBrief,
    plannedPosts,
    scheduledPostLabels,
  } = useAdminCampaignReviewData({ brandId, campaignId });

  const plannedPost = plannedPosts.find((post) => post.id === postId) ?? null;
  const postTasks = useMemo(
    () => tasks.filter((task) => task.scheduledPostId === postId),
    [postId, tasks],
  );
  const selectedTask = useMemo(
    () => postTasks.find((task) => task.id === selectedTaskId) ?? postTasks[0] ?? null,
    [postTasks, selectedTaskId],
  );

  const selectedTaskDetailsQuery = trpc.admin.brands.socialPostGenerationTask.useQuery(
    selectedTaskId
      ? { brandId: brandId ?? "", generationTaskId: selectedTaskId }
      : { brandId: brandId ?? "", generationTaskId: "00000000-0000-0000-0000-000000000000" },
    {
      enabled: !!brandId && !!selectedTaskId,
      refetchInterval: (query) => {
        const task = query.state.data;
        return task && (task.status === "queued" || task.status === "processing") ? 2000 : false;
      },
    },
  );
  const selectedTaskDetails = selectedTaskDetailsQuery.data ?? null;
  const selectedCarousel = selectedTaskDetails?.carousel ?? null;
  const selectedCarouselState = getCarouselReviewState(
    selectedCarousel,
    selectedTaskCarouselSlideId,
  );
  const selectedPlan =
    selectedCarouselState.selectedPlan ??
    (selectedCarousel
      ? null
      : (selectedTaskDetails?.result?.plan ?? selectedTask?.result?.plan ?? null));
  const selectedPlanBlocksFlat = useMemo(
    () => (selectedPlan ? getSceneBlocksFlat(selectedPlan) : []),
    [selectedPlan],
  );
  const selectedSceneBlock: SceneBlock | null =
    selectedPlanBlocksFlat.find((block) => block.id === selectedSceneBlockId) ?? null;

  useEffect(() => {
    if (!plannedPost) {
      setSelectedTaskId(null);
      return;
    }

    if (selectedTaskId && postTasks.some((task) => task.id === selectedTaskId)) {
      return;
    }

    setSelectedTaskId(
      plannedPost.output.latestAttempt?.generationTaskId ??
        plannedPost.output.latestCompleted?.generationTaskId ??
        postTasks[0]?.id ??
        null,
    );
  }, [plannedPost, postTasks, selectedTaskId]);

  useEffect(() => {
    setSelectedTaskCarouselSlideId(null);
  }, [selectedTask?.id]);

  useEffect(() => {
    if (!selectedPlan) {
      setSelectedSceneBlockId(null);
      return;
    }

    if (
      selectedSceneBlockId &&
      selectedPlanBlocksFlat.some((block) => block.id === selectedSceneBlockId)
    ) {
      return;
    }

    setSelectedSceneBlockId(selectedPlanBlocksFlat[0]?.id ?? null);
  }, [selectedPlan, selectedPlanBlocksFlat, selectedSceneBlockId]);

  const enqueueMutation = trpc.admin.brands.enqueueSocialPostGeneration.useMutation({
    onSuccess: async (data) => {
      setSelectedTaskId(data.generationTaskId);
      setNewAttemptSheetOpen(false);
      setHistoryOpen(true);
      await utils.admin.brands.socialPostGenerationTasks.invalidate({ brandId: brandId ?? "" });
      await utils.admin.brands.socialPostGenerationTask.invalidate({
        brandId: brandId ?? "",
        generationTaskId: data.generationTaskId,
      });
      await utils.admin.campaigns.get.invalidate({
        brandId: brandId ?? "",
        campaignId: campaignId ?? "",
      });
    },
  });

  if (!brandId || !campaignId || !postId) {
    return null;
  }

  if (campaignQuery.isPending || brandQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (campaignQuery.error || !campaign) {
    return <Navigate to={`/brands/${brandId}/campaigns`} replace />;
  }

  if (!brand) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <p className="text-lg font-medium">Failed to load brand</p>
        <p className="text-sm text-muted-foreground">{brandQuery.error?.message}</p>
      </div>
    );
  }

  if (!plannedPost) {
    return <Navigate to={buildCampaignDetailPath(brandId, campaignId)} replace />;
  }

  const selectedScheduledPostHasActiveRun = Boolean(
    plannedPost.output.latestAttempt &&
    (plannedPost.output.latestAttempt.status === "queued" ||
      plannedPost.output.latestAttempt.status === "processing"),
  );
  const plannerDisabled =
    brand.status !== "scanned" || enqueueMutation.isPending || selectedScheduledPostHasActiveRun;
  const statusConfig = PLANNED_POST_STATUS_CONFIG[plannedPost.status];
  const reviewAttempt = plannedPost.output.latestCompleted ?? plannedPost.output.latestAttempt;
  const latestCopy = plannedPost.output.latestCopy;
  const latestCopyMetadata = latestCopy?.result?.metadata ?? null;
  const reviewCarousel = plannedPost.output.latestCompleted?.carousel ?? null;
  const reviewCarouselState = getCarouselReviewState(reviewCarousel, reviewCarouselSlideId);
  const reviewPlan =
    reviewCarouselState.selectedPlan ??
    (reviewCarousel ? null : (plannedPost.output.latestCompleted?.result?.plan ?? null));
  const reviewBrandkit = plannedPost.output.latestCompleted?.preview?.brandkit ?? null;
  const reviewPreviewAsset =
    reviewCarouselState.selectedAssets[0] ?? plannedPost.output.latestCompleted?.assets[0] ?? null;
  const reviewLinkedAssets = reviewCarouselState.isCarousel
    ? reviewCarouselState.selectedAssets
    : (plannedPost.output.latestCompleted?.assets ?? []);
  const summaryText =
    plannedPost.output.latestCompleted?.summary ??
    reviewPlan?.creativeDirection.summary ??
    plannedPost.output.latestAttempt?.lastError ??
    "No result summary yet.";
  const rationaleText = reviewPlan?.creativeDirection.rationale ?? "No creative rationale yet.";
  const creativeObjectiveText =
    reviewPlan?.creativeDirection.objective ?? "No creative objective yet.";
  const canInspect = Boolean(selectedTask);
  const shouldShowNotes = Boolean(
    reviewPlan || summaryText || plannedPost.output.latestAttempt?.lastError,
  );

  const primaryAction = getPlannedPostPrimaryActionState({
    postStatus: plannedPost.status,
    hasAttempt: postTasks.length > 0,
    hasCompleted: Boolean(plannedPost.output.latestCompleted),
    hasActiveRun: selectedScheduledPostHasActiveRun,
  });
  const latestFailedAttempt =
    plannedPost.output.latestAttempt?.status === "failed" ? plannedPost.output.latestAttempt : null;
  const canRetryLatestAttempt = canRetryFailedGeneration({
    brandStatus: brand.status,
    taskStatus: latestFailedAttempt?.status,
    hasActiveRun: selectedScheduledPostHasActiveRun,
    hasRequest: Boolean(latestFailedAttempt?.request),
  });

  const handleSubmit = () => {
    enqueueMutation.mutate({
      brandId,
      campaignId,
      scheduledPostId: plannedPost.id,
      request: {
        type: "social_post.request",
        version: 3,
        sourceBrief: {
          version: 1,
          origin: "campaign",
          campaignId: campaign.id,
          campaignName: campaign.name,
          campaignBrief,
          slot: plannedPost.slot,
          additionalDirection: additionalDirection.trim() || null,
        },
        platform: plannedPost.slot.platform,
        postType: plannedPost.slot.postType,
        variantCount: 1,
      },
    });
  };

  const handleRetryLatestAttempt = () => {
    if (!latestFailedAttempt || !canRetryLatestAttempt) {
      return;
    }

    enqueueMutation.mutate({
      brandId,
      campaignId,
      scheduledPostId: plannedPost.id,
      request: latestFailedAttempt.request,
    });
  };

  return (
    <div className="-m-6 min-h-[calc(100vh-3.5rem)] bg-[#f7f5ef]">
      <header className="sticky top-0 z-20 border-b bg-background/95 px-6 py-3 shadow-xs backdrop-blur lg:px-8">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-3 w-fit shrink-0 gap-2 text-muted-foreground"
              asChild
            >
              <Link to={buildCampaignDetailPath(brandId, campaignId)}>
                <ChevronLeft className="size-4" />
                Campaign board
              </Link>
            </Button>
            <div className="hidden h-8 w-px bg-border lg:block" />
            <div className="flex min-w-0 flex-wrap items-center gap-2.5">
              <StatusPill
                label={statusConfig?.label ?? plannedPost.status}
                className={statusConfig?.className}
              />
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {formatDate(plannedPost.scheduledFor)}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {plannedPost.attemptCount} {plannedPost.attemptCount === 1 ? "attempt" : "attempts"}
              </span>
            </div>
          </div>
          <div className="min-w-0 text-sm text-muted-foreground xl:max-w-xl xl:text-right">
            <span className="font-medium text-foreground">{campaign.name}</span>
            <span className="mx-2 text-foreground/35">/</span>
            <span>{getPostTypeLabel(plannedPost.slot.postType)}</span>
          </div>
        </div>
      </header>

      <main className="grid gap-5 px-6 py-5 lg:px-8 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="min-w-0 space-y-5">
          <div className="space-y-3 rounded-[2rem] border border-border/60 bg-background px-5 py-5 shadow-sm lg:px-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Planned post
            </div>
            <h1 className="max-w-5xl text-balance text-3xl font-semibold tracking-[-0.035em] text-foreground lg:text-5xl">
              {plannedPost.slot.concept}
            </h1>
            <p className="max-w-4xl text-[15px] leading-7 text-muted-foreground">
              {plannedPost.slot.callToAction ?? campaignBrief.summary}
            </p>
          </div>

          <Card className="overflow-hidden border-border/60 bg-card shadow-sm">
            <CardHeader className="border-b border-border/60 bg-background/70 px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Review Creative</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Judge the final render first. Production metadata stays out of the way.
                  </p>
                </div>
                {reviewAttempt ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <TaskStatusBadge status={reviewAttempt.status} />
                    <span>{formatDate(reviewAttempt.updatedAt)}</span>
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-4 p-3 lg:p-4">
              {(plannedPost.output.latestAttempt?.lastError &&
                !plannedPost.output.latestCompleted) ||
              plannedPost.output.latestCompleted ? (
                <ReviewStatusNotice
                  latestAttempt={plannedPost.output.latestAttempt}
                  latestCompleted={plannedPost.output.latestCompleted}
                />
              ) : null}

              <div className="overflow-hidden rounded-[32px] border bg-[radial-gradient(circle_at_top,hsl(var(--background))_0,hsl(var(--muted))_78%)] px-3 py-5 lg:px-8 lg:py-8">
                <CarouselSlideRail
                  carousel={reviewCarousel}
                  selectedSlideId={reviewCarouselState.selectedSlideId}
                  onSelectSlideId={setReviewCarouselSlideId}
                  className="mx-auto mb-5 max-w-5xl"
                />
                {reviewPlan && reviewBrandkit ? (
                  <SceneTemplatePreview
                    plan={reviewPlan}
                    linkedAssets={reviewLinkedAssets}
                    brandkit={reviewBrandkit}
                    showCanvasMeta={false}
                    maxWidth={1040}
                  />
                ) : reviewPreviewAsset ? (
                  <img
                    src={reviewPreviewAsset.signedUrl}
                    alt={`Generated campaign creative for ${plannedPost.slot.concept}`}
                    width={reviewPreviewAsset.width ?? 1080}
                    height={reviewPreviewAsset.height ?? 1350}
                    className="mx-auto block h-auto max-h-[76rem] w-full max-w-6xl object-contain"
                    loading="lazy"
                  />
                ) : (
                  <EmptyReviewState postStatus={plannedPost.status} />
                )}
                <CarouselSlideSummary
                  carousel={reviewCarousel}
                  selectedSlideId={reviewCarouselState.selectedSlideId}
                  className="mx-auto mt-5 max-w-5xl"
                />
              </div>
              <CarouselCopyPanel carousel={reviewCarousel} />
            </CardContent>
          </Card>

          {latestCopy ? (
            <Card className="border-border/60 bg-card shadow-sm">
              <CardHeader className="border-b border-border/60 bg-background/70 px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Publish Copy</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Caption, hashtags, alt text, and trace for the latest copywriter run.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <TaskStatusBadge status={latestCopy.status} />
                    <span>{formatDate(latestCopy.updatedAt)}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-5 lg:p-6">
                {latestCopy.lastError ? (
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {latestCopy.lastError}
                  </div>
                ) : null}
                {latestCopyMetadata ? (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <DetailBlock label="Caption" value={latestCopyMetadata.caption} />
                    <div className="space-y-1">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
                        Hashtags
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {latestCopyMetadata.hashtags.map((hashtag) => (
                          <span
                            key={hashtag}
                            className="rounded-full bg-muted px-2.5 py-1 font-mono text-xs text-foreground/72"
                          >
                            #{hashtag.replace(/^#/, "")}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="lg:col-span-2">
                      <DetailBlock label="Alt Text" value={latestCopyMetadata.altText} />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Copy metadata has not completed for this planned post yet.
                  </p>
                )}
                <CopyTracePanel
                  trace={latestCopy.result?.copyTrace}
                  title="Why This Publish Copy"
                />
              </CardContent>
            </Card>
          ) : null}

          {shouldShowNotes ? (
            <Card className="border-border/60 bg-card shadow-sm">
              <CardContent className="space-y-4 p-5 lg:p-6">
                <DetailBlock label="Result Summary" value={summaryText} />
                <Collapsible open={reviewNotesOpen} onOpenChange={setReviewNotesOpen}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                    >
                      {reviewNotesOpen ? "Hide why this works" : "Show why this works"}
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform",
                          reviewNotesOpen ? "rotate-180" : "rotate-0",
                        )}
                        aria-hidden="true"
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="grid gap-4 pt-2 lg:grid-cols-2">
                    <DetailBlock label="Creative Objective" value={creativeObjectiveText} />
                    <DetailBlock label="Creative Rationale" value={rationaleText} />
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          ) : null}
        </section>

        <aside className="space-y-4 2xl:sticky 2xl:top-5 2xl:self-start">
          <Card className="border-border/60 bg-card shadow-sm">
            <CardHeader className="border-b border-border/60 pb-3">
              <CardTitle className="text-base">Next Action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4">
              {latestFailedAttempt ? (
                <Button
                  type="button"
                  className="w-full rounded-xl"
                  disabled={!canRetryLatestAttempt || enqueueMutation.isPending}
                  onClick={handleRetryLatestAttempt}
                >
                  {enqueueMutation.isPending ? "Retrying..." : "Retry Failed Attempt"}
                </Button>
              ) : null}

              {primaryAction.kind === "generate" ? (
                <GeneratePlannedPostSheet
                  open={newAttemptSheetOpen}
                  onOpenChange={setNewAttemptSheetOpen}
                  triggerLabel={primaryAction.label}
                  campaignName={campaign.name}
                  campaignBrief={campaignBrief}
                  plannedPosts={plannedPosts}
                  selectedScheduledPostId={plannedPost.id}
                  onSelectScheduledPostId={() => {}}
                  additionalDirection={additionalDirection}
                  onAdditionalDirectionChange={setAdditionalDirection}
                  brandStatus={brand.status}
                  selectedScheduledPostHasActiveRun={selectedScheduledPostHasActiveRun}
                  lockSelection={true}
                  disabled={plannerDisabled}
                  isPending={enqueueMutation.isPending}
                  errorMessage={enqueueMutation.error?.message}
                  onSubmit={handleSubmit}
                />
              ) : (
                <div className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3 text-sm leading-6 text-foreground/68">
                  {primaryAction.label}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card shadow-sm">
            <CardHeader className="border-b border-border/60 pb-3">
              <CardTitle className="text-base">Brief</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4">
              <DetailBlock
                label="Objective"
                value={plannedPost.slot.objectiveLabel ?? plannedPost.slot.objective}
              />
              <DetailBlock
                label="Audience"
                value={plannedPost.slot.audienceSegment ?? "General audience"}
              />
              <DetailBlock label="Format" value={getPostTypeLabel(plannedPost.slot.postType)} />
              <DetailBlock
                label="CTA"
                value={plannedPost.slot.callToAction ?? "No CTA specified"}
              />
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card shadow-sm">
            <CardHeader className="border-b border-border/60 pb-3">
              <CardTitle className="text-base">Attempt Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4">
              <CurrentAttemptSummary
                selectedTask={selectedTask}
                selectedTaskDetails={selectedTaskDetails}
              />

              <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-between rounded-xl text-muted-foreground"
                  >
                    {historyOpen ? "Hide attempts" : `Show attempts (${postTasks.length})`}
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform",
                        historyOpen ? "rotate-180" : "rotate-0",
                      )}
                      aria-hidden="true"
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-3">
                  {tasksQuery.isPending ? (
                    <div className="space-y-3">
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  ) : (
                    <PlannerTaskList
                      tasks={postTasks}
                      selectedTaskId={selectedTask?.id ?? null}
                      onSelect={setSelectedTaskId}
                      scheduledPostLabels={scheduledPostLabels}
                    />
                  )}
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>

          {canInspect ? (
            <Card className="border-border/60 bg-card shadow-sm">
              <CardHeader className="border-b border-border/60 pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Technical Inspection</CardTitle>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Opens a full-screen workbench with creative and technical views.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 rounded-xl"
                    onClick={() => setInspectOpen(true)}
                  >
                    <Layers3 className="size-4" />
                    Open
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Layers, block properties, brandkit, JSON, and cost details stay out of the review
                flow until needed.
              </CardContent>
            </Card>
          ) : null}
        </aside>
      </main>

      {canInspect && inspectOpen ? (
        <section
          className="fixed inset-0 z-50 flex flex-col bg-background"
          aria-label="Inspection Workspace"
        >
          <div className="flex shrink-0 flex-col gap-3 border-b bg-background px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:px-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <Layers3 className="size-4" aria-hidden="true" />
                Inspection Workspace
              </div>
              <h2 className="mt-1 truncate text-lg font-semibold text-foreground">
                {plannedPost.slot.concept}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedTaskDetails?.brandkit ? (
                <Button
                  variant={brandkitOpen ? "default" : "outline"}
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setBrandkitOpen((current) => !current)}
                >
                  {brandkitOpen ? "Hide Brandkit" : "Show Brandkit"}
                </Button>
              ) : null}
              <Tabs value={view} onValueChange={(next) => setView(next as PlannerView)}>
                <TabsList className="h-9">
                  <TabsTrigger value="creative" className="px-3 text-xs">
                    Creative
                  </TabsTrigger>
                  <TabsTrigger value="technical" className="px-3 text-xs">
                    Technical
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 rounded-xl"
                onClick={() => setInspectOpen(false)}
              >
                <X className="size-4" />
                Close
              </Button>
            </div>
          </div>
          {selectedTaskDetails?.brandkit && brandkitOpen ? (
            <div className="max-h-64 shrink-0 overflow-auto border-b bg-muted/20 p-4 lg:p-5">
              <BrandkitPanel brandkit={selectedTaskDetails.brandkit ?? EMPTY_BRANDKIT} />
            </div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-hidden">
            <PlannerOutput
              view={view}
              selectedTask={selectedTask}
              selectedTaskDetails={selectedTaskDetails}
              selectedTaskDetailsQuery={{
                isPending: selectedTaskDetailsQuery.isPending,
                error: selectedTaskDetailsQuery.error
                  ? { message: selectedTaskDetailsQuery.error.message }
                  : null,
              }}
              selectedPlan={selectedPlan}
              carousel={selectedCarousel}
              selectedCarouselSlideId={selectedCarouselState.selectedSlideId}
              selectedSceneBlock={selectedSceneBlock}
              selectedSceneBlockId={selectedSceneBlockId}
              onSelectCarouselSlideId={setSelectedTaskCarouselSlideId}
              onSelectSceneBlock={setSelectedSceneBlockId}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
